import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { User as UserEntity } from '@/api/entities';
import { FlightBooking } from '@/api/entities';
import { AlertCircle, TrendingUp, DollarSign, Calendar, CalendarClock, PlaneTakeoff, Info, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { format, parseISO, startOfDay, endOfDay, subDays, startOfMonth, startOfYear, subMonths, differenceInCalendarDays } from 'date-fns';
import { getCityName } from '@/components/airportUtils';
import { historicalGains, totalHistoricalGains, totalCapitalInvested } from '@/data/historicalInvestorGains';
import { fmtAmount } from '@/utils/money';
// The customer-facing price breakdown, derived on travel totals (a Faye
// premium is never base fare a backer earns on).
import { deriveBookingPricing } from '@/utils/investorBookingPricing';
import {
  DEFERA_CAPITAL_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE,
  DEFERA_ZERO_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE,
  SLICE_PAY_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE,
  calculatePaymentSchedule,
  getDefaultInstallmentDepositPercentForLeadDays,
} from '@/utils/paymentScheduleCalculator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { resolveBackerTerms, BACKER_MODEL_SERVICE_FEE, DEFAULT_BACKER_RATE, INVESTOR_FEE_RATE_ON_SERVICE_FEE } from '@/utils/backerEarnings';
import { MILES_LIABILITY_CENTS_PER_MILE } from '../../supabase/functions/_shared/miles-redemption.js';

// Helper function to convert time range code to display text
/** Match Dashboard/Analytics status resolution — some rows store status only in flight_data.trip_status. */
function resolveBookingStatus(booking) {
  const flightData = booking?.flight_data || booking?.flightData || {};
  return booking?.status || booking?.trip_status || flightData.trip_status || 'pending';
}

function isActiveInvestorTripStatus(status) {
  return status === 'confirmed' || status === 'completed';
}

const getTimeRangeLabel = (range) => {
  const labels = {
    '1D': '1 day',
    '1W': '1 week',
    '1M': '1 month',
    '3M': '3 months',
    'YTD': 'Year to date',
    '1Y': '1 year',
    'ALL': 'All time'
  };
  return labels[range] || range;
};

export default function InvestorPage() {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [trips, setTrips] = useState([]);
  const [dailyEarnings, setDailyEarnings] = useState([]);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [periodEarnings, setPeriodEarnings] = useState(0);
  const [timeRange, setTimeRange] = useState('1M');
  const [withdrawableBalance, setWithdrawableBalance] = useState(0);
  const [totalGains, setTotalGains] = useState(0);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [viewAsUser, setViewAsUser] = useState(null); // For admin: view as specific backer/investor
  const [backersAndInvestors, setBackersAndInvestors] = useState([]); // List of backers and investors for dropdown
  const [bookingsToShow, setBookingsToShow] = useState(50); // Number of bookings to display
  const [isBackerUser, setIsBackerUser] = useState(false); // Track if current user is a backer
  const [backerEarningModel, setBackerEarningModel] = useState('service_fee_share'); // 'service_fee_share' (default 25%) or 'base_fare_share' (e.g. 15%)
  const [backerEarningRate, setBackerEarningRate] = useState(0.25); // Default share rate
  const [backerStartingBalance, setBackerStartingBalance] = useState(0);
  const [backerStartingBalanceDate, setBackerStartingBalanceDate] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const user = await UserEntity.me();
        setCurrentUser(user);

        // SECURITY: Check app_metadata.roles array for admin, investor, or backer
        // Fallback to user.role if app_metadata is not yet populated
        const roles = user.app_metadata?.roles || [];
        const isAdmin = roles.includes('admin') || user.role === 'admin';
        const isInvestor = roles.includes('investor') || user.role === 'investor';
        const isBacker = roles.includes('backer');
        
        if (!isAdmin && !isInvestor && !isBacker) {
          setIsLoading(false);
          return;
        }

        // For admins, load list of backers and investors for view switcher
        let selectedUser = null;
        if (isAdmin) {
          try {
            const { User } = await import('@/api/entities');
            const allUsers = await User.list();
            const backersAndInvestorsList = allUsers.filter(u => {
              const userRoles = u.app_metadata?.roles || [];
              // Also check user.role as fallback (backend returns role field)
              const userRole = u.role;
              const isBacker = userRoles.includes('backer') || userRole === 'backer';
              const isInvestor = userRoles.includes('investor') || userRole === 'investor';
              return isBacker || isInvestor;
            });
            console.log('📊 Loaded backers/investors:', backersAndInvestorsList.length, backersAndInvestorsList.map(u => ({ 
              name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email, 
              roles: u.app_metadata?.roles, 
              role: u.role 
            })));
            setBackersAndInvestors(backersAndInvestorsList);
            
            // If admin is viewing as a specific user, get that user's details
            if (viewAsUser) {
              selectedUser = backersAndInvestorsList.find(u => u.id === viewAsUser);
            }
          } catch (error) {
            console.error('Failed to load backers/investors list:', error);
            setBackersAndInvestors([]);
          }
        }
        
        // Determine effective user for filtering (admin can view as specific user)
        // MUST be defined before use in console.log below
        const effectiveUserId = (isAdmin && viewAsUser && selectedUser) ? viewAsUser : user.id;
        const effectiveAppMetadata = (isAdmin && viewAsUser && selectedUser)
          ? (selectedUser.app_metadata || {})
          : (user.app_metadata || {});
        // When admin "views as" a backer whose app_metadata.roles is empty but
        // user.role is set (legacy cache), fall back to that role so filtering works.
        const selectedUserRole = (isAdmin && viewAsUser && selectedUser) ? selectedUser.role : null;
        const effectiveUserRoles = (effectiveAppMetadata.roles?.length
          ? effectiveAppMetadata.roles
          : (selectedUserRole === 'backer'
              ? ['backer']
              : selectedUserRole === 'investor'
                ? ['investor']
                : roles));
        const effectiveIsBacker = effectiveUserRoles.includes('backer');
        const effectiveIsInvestor = effectiveUserRoles.includes('investor') || (!effectiveIsBacker && (isInvestor || isAdmin));
        // Pure backers already receive only their assigned rows from
        // /api/admin/all-bookings (server-side backer_id filter). Re-filtering
        // client-side breaks when PostgREST omits backer_id from the payload even
        // though the row matched — trips vanish from the Earnings page entirely.
        const isPureBackerScope = isBacker && !isAdmin && !isInvestor;

        // Per-backer earning configuration (stored in app_metadata, admin-only editable).
        // This only changes the backer's analytics/earnings — customer-facing pricing
        // (hold fee, service fee, totals) is unchanged.
        //   backer_earning_model: 'service_fee_share' (default, 25% of service fee) |
        //                         'base_fare_share' (e.g. 15% of base fare)
        //   backer_earning_rate: multiplier applied to the chosen base (defaults: 0.25 or 0.15)
        //   backer_starting_balance: seed credit applied on backer_starting_balance_date
        // Terms come from the shared resolver so this page, Analytics, the
        // rankings endpoint and the assignment scripts cannot drift apart on
        // what a backer is owed. This page was the only surface reading
        // app_metadata at all; the others inferred it, and one matched Ravi by
        // hardcoded email.
        const { model: earningModel, rate: earningRate } = effectiveIsBacker
          ? resolveBackerTerms(effectiveAppMetadata)
          : { model: BACKER_MODEL_SERVICE_FEE, rate: DEFAULT_BACKER_RATE[BACKER_MODEL_SERVICE_FEE] };
        const startingBalance = effectiveIsBacker
          ? parseFloat(effectiveAppMetadata.backer_starting_balance || 0)
          : 0;
        const startingBalanceDate = effectiveIsBacker
          ? (effectiveAppMetadata.backer_starting_balance_date || null)
          : null;

        // Store in state for use in modal/render
        setIsBackerUser(effectiveIsBacker);
        setBackerEarningModel(earningModel);
        setBackerEarningRate(earningRate);
        setBackerStartingBalance(startingBalance);
        setBackerStartingBalanceDate(startingBalanceDate);
        
        // Load all confirmed bookings (admin/investor see all, backers see only their assigned trips)
        const bookingsData = await FlightBooking.list();
        console.log('🔍 [Investor] Raw bookings data loaded:', {
          totalBookings: bookingsData?.length || 0,
          isBacker,
          effectiveUserId,
          userEmail: user.email
        });
        
        // Include BOTH 'confirmed' (still being paid off) and 'completed' (admin
        // marked "Trip is Paid Off!"). Previously this filter only included
        // 'confirmed', which made completed trips invisible to backer earnings
        // analytics and to the Available Balance calc — meaning earnings could
        // never become withdrawable because the trips that paid them out were
        // dropped from the dataset. Cancelled/defaulted trips remain excluded
        // (per the "free on cancel" product rule and to match historical
        // behavior — no earnings counted, no capital locked).
        let confirmedTrips = (bookingsData || []).filter((b) =>
          isActiveInvestorTripStatus(resolveBookingStatus(b))
        );
        console.log('🔍 [Investor] Confirmed+completed trips after status filter:', confirmedTrips.length);
        
        // Debug: Log ALL bookings with backer_id (not just first one)
        if (confirmedTrips.length > 0) {
          const bookingsWithBacker = confirmedTrips.filter(t => t.backer_id || t.backerId);
          console.log('🔍 [Investor] Bookings with backer_id assigned:', {
            total: bookingsWithBacker.length,
            backerIds: [...new Set(bookingsWithBacker.map(b => b.backer_id || b.backerId))],
            sampleBookings: bookingsWithBacker.slice(0, 5).map(b => ({
              id: b.id,
              backer_id: b.backer_id,
              backerId: b.backerId,
              trip_reference: b.tripReference || b.trip_reference
            }))
          });
          
          // Log sample booking structure
          console.log('🔍 [Investor] Sample booking object structure:', {
            id: confirmedTrips[0].id,
            backer_id: confirmedTrips[0].backer_id,
            backerId: confirmedTrips[0].backerId,
            hasBackerIdField: 'backer_id' in confirmedTrips[0],
            allKeys: Object.keys(confirmedTrips[0]).filter(k => k.toLowerCase().includes('backer')),
            rawData: confirmedTrips[0]
          });
        }
        
        // Filter by backer_id for backer users (only show trips assigned to them)
        // CRITICAL: If user is a backer (even if also an investor), they should only see trips assigned to them
        // Investors who are NOT backers see all trips (no filtering needed)
        // If admin is viewing as a backer, filter by that backer's ID
        if (effectiveIsBacker && !isPureBackerScope) {
          console.log('🔍 [Investor] Filtering trips for backer:', {
            effectiveUserId,
            totalTrips: confirmedTrips.length,
            isBacker,
            isAdmin,
            isInvestor,
            effectiveIsBacker,
            isPureBackerScope,
            userRoles: roles
          });
          
          // Log sample trips to debug backer_id field
          if (confirmedTrips.length > 0) {
            console.log('🔍 [Investor] Sample trip data (first 3):', confirmedTrips.slice(0, 3).map(t => ({
              id: t.id,
              backer_id: t.backer_id,
              backerId: t.backerId,
              hasBackerId: !!(t.backer_id || t.backerId)
            })));
          }
          
          confirmedTrips = confirmedTrips.filter(trip => {
            // Check multiple field name variations and handle UUID/string comparison
            const tripBackerId = trip.backer_id || trip.backerId || null;
            
            // Handle both UUID and string comparisons
            const matches = tripBackerId && (
              String(tripBackerId).toLowerCase() === String(effectiveUserId).toLowerCase() ||
              tripBackerId === effectiveUserId
            );
            
            // Log each trip being checked for debugging
            if (confirmedTrips.length <= 10) { // Only log if small number of trips
              console.log('🔍 [Investor] Checking trip:', {
                tripId: trip.id,
                tripBackerId,
                effectiveUserId,
                matches,
                tripBackerIdType: typeof tripBackerId,
                effectiveUserIdType: typeof effectiveUserId
              });
            }
            
            return matches;
          });
          
          console.log('✅ [Investor] Filtered trips for backer:', {
            before: bookingsData.length,
            after: confirmedTrips.length,
            effectiveUserId,
            filteredCount: confirmedTrips.length
          });
        } else if (effectiveIsBacker && isPureBackerScope) {
          console.log('✅ [Investor] Pure backer scope — trusting server-side backer_id filter', {
            totalTrips: confirmedTrips.length,
            effectiveUserId
          });
        }
        
        // Group by trip_reference to avoid double-counting segments
        const tripsByReference = new Map();
        confirmedTrips.forEach(trip => {
          const ref = trip.tripReference || trip.trip_reference || trip.bookingReference || trip.id;
          if (!tripsByReference.has(ref)) {
            tripsByReference.set(ref, trip);
          }
        });
        
        // Calculate earnings based on role and (for backers) the per-user earning model:
        //   - Investors: 14% of service fees on all trips
        //   - Backers (service_fee_share model): rate * service fee on assigned trips (default 25%)
        //   - Backers (base_fare_share model): rate * base fare on assigned trips (e.g. 15%)
        //
        // NOTE: customer-facing pricing (hold fee, service fee, total) is UNCHANGED.
        // This calculation only affects what the backer actually earns + analytics.
        //
        // Both `baseFare` and `serviceFee` come from `deriveBookingPricing()`, which
        // matches FlightDetailsModal: prefer `flight_data.base_amount`, fall back to
        // `grand_total_with_hold - service_fee - hold_fee`. This prevents Defera
        // Capital / Defera Zero trips from inflating `base_fare_share` earnings ~24%
        // by accidentally using the customer's gross total as the base.
        const tripsWithEarnings = Array.from(tripsByReference.values()).map(trip => {
          const pricing = deriveBookingPricing(trip);
          const serviceFee = pricing.serviceFee;
          const baseFare = pricing.baseFare;

          let earnings = 0;
          if (effectiveIsBacker) {
            if (earningModel === 'base_fare_share') {
              earnings = baseFare * earningRate;
            } else {
              // service_fee_share (default)
              earnings = serviceFee * earningRate;
            }
          } else {
            // Investors earn 14% of service fees on all trips
            earnings = serviceFee * INVESTOR_FEE_RATE_ON_SERVICE_FEE;
          }

          // Handle both camelCase and snake_case timestamps from API
          const rawCreated = trip.createdAt || trip.created_at || trip.created_at_utc;
          const createdDate = rawCreated ? parseISO(rawCreated) : new Date();

          // `effectiveDate` is the date this trip should show up on for the
          // CURRENT viewer's earnings chart:
          //   * Backers earn nothing on a trip until it's assigned to them, so
          //     their chart should bucket it on the assignment moment
          //     (`backer_assigned_at`, migration 135). Otherwise a trip booked
          //     in January and assigned to Ravi today would appear as a
          //     January earnings event, which is confusing and factually wrong
          //     — he wasn't earning on it in January.
          //   * Investors get 14% of every service fee regardless of who's
          //     assigned, so booking-date bucketing continues to be correct
          //     for them. Nothing changes.
          //   * Legacy rows without `backer_assigned_at` (backfilled to
          //     `created_at` in migration 135, but we still guard here for
          //     defense-in-depth) fall back to `createdDate` so the chart
          //     never renders a NaN bucket.
          const rawAssigned =
            trip.backer_assigned_at || trip.backerAssignedAt || null;
          const assignedDate = rawAssigned ? parseISO(rawAssigned) : null;
          const effectiveDate = effectiveIsBacker
            ? (assignedDate || createdDate)
            : createdDate;

          // A trip is "paid off" (and therefore its funds are released to the
          // backer) once an admin marks it status='completed' — see
          // server.js L15749 which sends the "Congrats! Your Trip is Paid Off!"
          // email on that transition. Until then the trip's base fare counts as
          // locked capital and its backer earnings remain non-withdrawable.
          const isPaidOff = resolveBookingStatus(trip) === 'completed';

          return {
            ...trip,
            investorEarnings: earnings, // Keep field name for compatibility
            earnings: earnings, // New field name
            baseFare, // exposed for locked-capital aggregation
            isPaidOff,
            createdDate,
            assignedDate,
            effectiveDate
          };
        });

        // Sort the trips list shown under the chart by effectiveDate desc:
        //   * for investors this matches the API's created_at desc ordering
        //     (effectiveDate === createdDate for them), so the ordering is
        //     unchanged;
        //   * for backers it groups recent assignments at the top of the
        //     list, matching how the chart itself buckets earnings — a trip
        //     assigned today appears at the top even if it was booked long
        //     ago, which is what Ravi expects to see.
        const sortedTrips = [...tripsWithEarnings].sort(
          (a, b) => (b.effectiveDate?.getTime?.() || 0) - (a.effectiveDate?.getTime?.() || 0)
        );
        setTrips(sortedTrips);

        // Calculate date range based on timeRange selection
        const now = new Date();
        let startDate, endDate = endOfDay(now);
        let daysInRange;
        
        if (timeRange === '1D') {
          startDate = startOfDay(now);
          daysInRange = 1;
        } else if (timeRange === '1W') {
          startDate = startOfDay(subDays(now, 6));
          daysInRange = 7;
        } else if (timeRange === '1M') {
          startDate = startOfDay(subDays(now, 29));
          daysInRange = 30;
        } else if (timeRange === '3M') {
          startDate = startOfDay(subMonths(now, 3));
          daysInRange = differenceInCalendarDays(endDate, startDate) + 1;
        } else if (timeRange === 'YTD') {
          startDate = startOfYear(now);
          daysInRange = differenceInCalendarDays(endDate, startDate) + 1;
        } else if (timeRange === '1Y') {
          startDate = startOfDay(subDays(now, 364));
          daysInRange = 365;
        } else { // ALL
          startDate = new Date('2024-06-01'); // Start from historical data
          daysInRange = differenceInCalendarDays(endDate, startDate) + 1;
        }

        // Create array for every day in range
        const allDays = Array.from({ length: daysInRange }, (_, i) => {
          const date = new Date(startDate);
          date.setDate(date.getDate() + i);
          return {
            date: format(date, 'MMM dd'),
            fullDate: startOfDay(date),
            dateKey: format(date, 'yyyy-MM-dd'),
            earnings: 0
          };
        });

        // Inject historical gains data (only for investors, not backers)
        if (!effectiveIsBacker) {
          historicalGains.forEach(historical => {
            const historicalDate = parseISO(historical.date);
            const dayIndex = allDays.findIndex(d => 
              d.dateKey === format(historicalDate, 'yyyy-MM-dd')
            );
            
            if (dayIndex !== -1) {
              allDays[dayIndex].earnings += historical.amount;
            }
          });
        }

        // For backers with a starting balance, inject the seed credit as a one-time
        // earnings entry on the configured date so it shows up in the chart + totals.
        if (effectiveIsBacker && startingBalance > 0 && startingBalanceDate) {
          const balanceDate = parseISO(startingBalanceDate);
          const dayIndex = allDays.findIndex(d =>
            d.dateKey === format(balanceDate, 'yyyy-MM-dd')
          );
          if (dayIndex !== -1) {
            allDays[dayIndex].earnings += startingBalance;
          }
        }

        // Aggregate real-time earnings by day. Uses `effectiveDate`
        // (assignment date for backers, booking date for investors — see the
        // long-form comment where effectiveDate is computed above) so a
        // backer's chart never buckets a trip before they were actually
        // assigned to it.
        tripsWithEarnings.forEach(trip => {
          const tripDate = startOfDay(trip.effectiveDate);
          const dayIndex = allDays.findIndex(d => 
            d.fullDate.getTime() === tripDate.getTime()
          );
          
          if (dayIndex !== -1) {
            allDays[dayIndex].earnings += trip.investorEarnings;
          }
        });

        // Calculate cumulative earnings for area chart (includes historical + real bookings for investors only)
        let cumulative = 0;
        
        // Add ALL earnings (historical CSV + real bookings) that happened before the window
        const windowStartDate = allDays[0]?.fullDate || new Date();
        
        const historicalBeforeWindow = effectiveIsBacker ? 0 : historicalGains
          .filter(h => parseISO(h.date) < windowStartDate)
          .reduce((sum, h) => sum + h.amount, 0);
        
        // Pre-window baseline for the cumulative area chart. Must match the
        // per-day bucketing above (effectiveDate), otherwise a backer's chart
        // would double-count trips whose booking date is before the window
        // but whose assignment date is inside it (or vice versa).
        const bookingsBeforeWindow = tripsWithEarnings
          .filter(trip => trip.effectiveDate < windowStartDate)
          .reduce((sum, trip) => sum + trip.investorEarnings, 0);

        // Backer seed credit: include it in the pre-window baseline if the seed
        // date is older than the current window's start date.
        const startingBalanceBeforeWindow = (
          effectiveIsBacker && startingBalance > 0 && startingBalanceDate &&
          parseISO(startingBalanceDate) < windowStartDate
        ) ? startingBalance : 0;

        cumulative = historicalBeforeWindow + bookingsBeforeWindow + startingBalanceBeforeWindow;
        
        const dailyData = allDays.map(day => {
          cumulative += day.earnings;
          return {
            date: day.date,
            earnings: parseFloat(day.earnings.toFixed(2)),
            cumulative: parseFloat(cumulative.toFixed(2))
          };
        });

        setDailyEarnings(dailyData);
        
        // Calculate earnings
        const currentEarnings = tripsWithEarnings.reduce((sum, trip) => sum + trip.investorEarnings, 0);
        
        if (effectiveIsBacker) {
          // Backers see:
          //   - Headline total (`totalEarnings`) = seed + ALL accrued earnings.
          //     This is Ravi's true portfolio value — what he has accumulated
          //     regardless of whether the trips that earned it are paid off
          //     yet. Showing only the liquid number here would visually erase
          //     the value he's currently "loaning out".
          //   - Gains (`totalGains`) = all accrued earnings (locked + free).
          //   - Available Balance (`withdrawableBalance`) = the liquid slice:
          //       seed + earnings(paid-off trips) − base_fare(active trips)
          //     The base fare of trips that haven't been marked
          //     status='completed' is treated as capital currently deployed
          //     into the trip (Ravi's funds paid the airline up-front while
          //     the customer keeps paying installments). It returns to
          //     Available the moment the trip is marked paid off — same moment
          //     the earnings on that trip become withdrawable. Cancelled trips
          //     are already excluded from the dataset entirely (filter above),
          //     so they neither lock capital nor count earnings — consistent
          //     with "funds free immediately on cancel".
          const lockedCapital = tripsWithEarnings
            .filter(t => !t.isPaidOff)
            .reduce((sum, t) => sum + (t.baseFare || 0), 0);
          const completedEarnings = tripsWithEarnings
            .filter(t => t.isPaidOff)
            .reduce((sum, t) => sum + (t.investorEarnings || 0), 0);

          const totalBackerValue = startingBalance + currentEarnings;
          const liquidBalance = startingBalance + completedEarnings - lockedCapital;

          setTotalEarnings(totalBackerValue);
          // Headline portfolio value (unchanged): seed + everything earned.
          setWithdrawableBalance(liquidBalance);
          setTotalGains(currentEarnings);
        } else {
          // Investors: Total includes capital + historical earnings + current earnings
          // Capital is shown in total value but tracked separately as non-withdrawable
          const totalEarningsOnly = totalHistoricalGains + currentEarnings;
          const totalValueWithCapital = totalCapitalInvested + totalEarningsOnly;
          
          // Set total earnings to show full value (capital + earnings)
          setTotalEarnings(totalValueWithCapital);
          
          // Set withdrawable balance and gains (earnings only, no capital)
          setWithdrawableBalance(totalEarningsOnly);
          setTotalGains(totalEarningsOnly);
        }
        
        // Period earnings is just the earnings within this time range (no capital)
        const periodTotal = allDays.reduce((sum, day) => sum + day.earnings, 0);
        setPeriodEarnings(periodTotal);
        setIsLoading(false);
      } catch (error) {
        console.error('Failed to load investor data:', error);
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
        setIsLoading(false);
      }
    };

    loadData();
  }, [timeRange, viewAsUser]);

  // Access control - check app_metadata.roles with fallback to user.role
  if (currentUser && !isLoading) {
    const roles = currentUser.app_metadata?.roles || [];
    const isAdmin = roles.includes('admin') || currentUser.role === 'admin';
    const isInvestor = roles.includes('investor') || currentUser.role === 'investor';
    const isBacker = roles.includes('backer');
    
    if (!isAdmin && !isInvestor && !isBacker) {
      return (
        <div className="min-h-[70vh] flex items-center justify-center text-center p-4">
          <motion.div 
            initial={{ opacity: 0, y: -20 }} 
            animate={{ opacity: 1, y: 0 }} 
            className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 rounded-3xl max-w-md"
          >
            <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-light text-white mb-2 tracking-tight">Access Denied</h2>
            <p className="text-white/60 text-sm font-normal">
              Investor access required. This page is restricted to authorized investors and administrators.
            </p>
          </motion.div>
        </div>
      );
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="text-white font-normal">Loading investor dashboard...</div>
      </div>
    );
  }

  // Calculate effective user roles for render (for conditional display)
  const roles = currentUser?.app_metadata?.roles || [];
  const isAdminRender = roles.includes('admin') || currentUser?.role === 'admin';
  const viewingAsUser = viewAsUser ? backersAndInvestors.find(u => u.id === viewAsUser) : null;
  const effectiveAppMetadataRender = (isAdminRender && viewingAsUser)
    ? (viewingAsUser.app_metadata || {})
    : (currentUser?.app_metadata || {});
  const effectiveUserRolesRender = effectiveAppMetadataRender.roles || roles;
  const effectiveIsBackerRender = effectiveUserRolesRender.includes('backer');
  const { model: effectiveBackerModelRender, rate: effectiveBackerRateRender } = effectiveIsBackerRender
    ? resolveBackerTerms(effectiveAppMetadataRender)
    : { model: BACKER_MODEL_SERVICE_FEE, rate: DEFAULT_BACKER_RATE[BACKER_MODEL_SERVICE_FEE] };
  const effectiveStartingBalanceRender = effectiveIsBackerRender
    ? parseFloat(effectiveAppMetadataRender.backer_starting_balance || 0)
    : 0;
  const isBaseFareBackerRender = effectiveIsBackerRender && effectiveBackerModelRender === 'base_fare_share';

  return (
    <div className="min-h-screen bg-black p-4 md:p-6">
      <div className="max-w-2xl mx-auto">
        {/* Robinhood-Style Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-light text-white tracking-tight">
                {effectiveIsBackerRender ? 'Earnings' : 'Investing'}
              </h1>
              {/* Show current view mode for admin */}
              {isAdminRender && viewAsUser && viewingAsUser && (() => {
                const userRoles = viewingAsUser.app_metadata?.roles || [];
                const isBacker = userRoles.includes('backer');
                const roleLabel = isBacker ? 'Backer' : 'Investor';
                const displayName = (viewingAsUser.firstName && viewingAsUser.lastName)
                  ? `${viewingAsUser.firstName} ${viewingAsUser.lastName}`
                  : viewingAsUser.email?.split('@')[0] || viewingAsUser.email;
                return (
                  <p className="text-xs text-white/60 mt-1">
                    Viewing as: {displayName} ({roleLabel})
                  </p>
                );
              })()}
            </div>
            {/* Admin View-As Dropdown */}
            {(() => {
              const roles = currentUser?.app_metadata?.roles || [];
              const isAdmin = roles.includes('admin') || currentUser?.role === 'admin';
              if (isAdmin) {
                return (
                  <Select value={viewAsUser || 'all'} onValueChange={(value) => setViewAsUser(value === 'all' ? null : value)}>
                    <SelectTrigger className="w-[200px] bg-white/5 border-white/10 text-white text-sm">
                      <SelectValue placeholder="View as..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">View All (Admin)</SelectItem>
                      {backersAndInvestors.length > 0 ? (
                        backersAndInvestors.map(user => {
                          const userRoles = user.app_metadata?.roles || [];
                          const isBacker = userRoles.includes('backer');
                          const isInvestor = userRoles.includes('investor');
                          const roleLabel = isBacker ? 'Backer' : (isInvestor ? 'Investor' : '');
                          const displayName = (user.firstName && user.lastName)
                            ? `${user.firstName} ${user.lastName}`
                            : user.email?.split('@')[0] || user.email;
                          return (
                            <SelectItem key={user.id} value={user.id}>
                              {displayName} ({roleLabel})
                            </SelectItem>
                          );
                        })
                      ) : (
                        <SelectItem value="loading" disabled>Loading backers/investors...</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                );
              }
              return null;
            })()}
          </div>
          
          {/* Large Total Amount - Robinhood Style - Shows Total Portfolio Value (Capital + Gains) */}
          <div className="text-4xl md:text-5xl font-medium text-white tracking-tight mb-3">
            ${totalEarnings.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          
          {/* Gain Indicator - Green with up arrow and percentage (Robinhood style) */}
          <div className="flex items-center gap-2">
            <div className="flex items-center text-green-400 text-base font-normal">
              <span className="mr-1">▲</span>
              {timeRange === 'ALL' ? (
                <>
                  ${totalGains.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  {!effectiveIsBackerRender && (
                    <span className="ml-2">
                      ({totalCapitalInvested > 0 ? ((totalGains / totalCapitalInvested) * 100).toFixed(2) : '0.00'}%)
                    </span>
                  )}
                </>
              ) : (
                <>
                  ${periodEarnings.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  {!effectiveIsBackerRender && (
                    <span className="ml-2">
                      ({totalGains > 0 ? ((periodEarnings / totalGains) * 100).toFixed(2) : '0.00'}%)
                    </span>
                  )}
                </>
              )}
            </div>
            <span className="text-white/60 text-base font-normal">{getTimeRangeLabel(timeRange)}</span>
          </div>
        </motion.div>

        {/* Investment Graph - Robinhood Style (No Border, Clean) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-12"
        >
          <div className="h-[400px] md:h-[500px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dailyEarnings} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="earningsGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.4}/>
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis 
                  dataKey="date" 
                  stroke="#ffffff30" 
                  style={{ fontSize: '11px', fontWeight: '500' }}
                  tickLine={false}
                  axisLine={false}
                  hide={true}
                />
                <YAxis 
                  stroke="transparent"
                  style={{ fontSize: '11px', fontWeight: '500' }}
                  tickFormatter={(value) => `$${value}`}
                  tickLine={false}
                  axisLine={false}
                  domain={(() => {
                    if (dailyEarnings.length === 0) return [0, 'auto'];
                    
                    const values = dailyEarnings.map(d => d.cumulative);
                    const minValue = Math.min(...values);
                    const maxValue = Math.max(...values);
                    const range = maxValue - minValue;
                    
                    // If range is very small (less than 10% of max value), zoom in
                    // This makes small gains visible even when capital is large
                    if (range < maxValue * 0.1) {
                      const padding = range * 0.2; // Add 20% padding
                      return [
                        Math.max(0, minValue - padding),
                        maxValue + padding
                      ];
                    }
                    
                    // Otherwise use auto scaling
                    return [0, 'auto'];
                  })()}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#000000f5', 
                    border: '1px solid #ffffff15',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontWeight: '500',
                    padding: '8px 12px'
                  }}
                  formatter={(value) => [`$${parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, effectiveIsBackerRender ? 'Earnings' : 'Investing']}
                  cursor={{ stroke: '#10b981', strokeWidth: 1, strokeDasharray: '3 3' }}
                />
                <Area 
                  type="monotone" 
                  dataKey="cumulative" 
                  stroke="#10b981" 
                  strokeWidth={2.5}
                  fill="url(#earningsGradient)" 
                  animationDuration={1500}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          
          {/* Time Range Buttons - Robinhood Style (Below Graph) */}
          <div className="flex items-center justify-center gap-1.5 md:gap-2 mt-6">
            {['1D', '1W', '1M', '3M', 'YTD', '1Y', 'ALL'].map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-3 md:px-4 py-1.5 text-xs md:text-sm font-normal rounded-lg transition-all ${
                  timeRange === range
                    ? 'bg-green-500/20 text-green-400'
                    : 'text-white/60 hover:text-white/80'
                }`}
              >
                {range}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Breakdown - Robinhood Style */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mb-12"
        >
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-white font-light text-xl tracking-tight">Portfolio Breakdown</h2>
            <Dialog>
              <DialogTrigger asChild>
                <button className="text-white/60 hover:text-white transition-colors">
                  <Info className="w-4 h-4" />
                </button>
              </DialogTrigger>
              <DialogContent className="bg-brand-950/95 border-brand-800/45 backdrop-blur-xl text-white max-w-md rounded-3xl">
                <DialogHeader>
                  <DialogTitle className="text-white font-light text-lg">
                    {isBaseFareBackerRender ? 'Backer Earnings' : 'Service Fee Structure'}
                  </DialogTitle>
                  <DialogDescription className="text-white/60 text-sm font-normal mt-2">
                    {isBaseFareBackerRender
                      ? `Your earnings are ${Math.round(effectiveBackerRateRender * 100)}% of the base fare on each booking assigned to you. (Service fee and hold fee that the customer pays are separate and do not affect your earnings.)`
                      : effectiveIsBackerRender
                        ? `Backer earnings are ${Math.round(effectiveBackerRateRender * 100)}% of service fees collected on each booking.`
                        : 'Investor earnings are 14% of service fees collected on each booking.'}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 mt-4">
                  {isBaseFareBackerRender ? (
                    <>
                      <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                        <p className="text-white/60 text-xs font-normal mb-1">How your earnings are calculated</p>
                        <p className="text-white text-sm font-medium">{Math.round(effectiveBackerRateRender * 100)}% × Base Fare</p>
                        <p className="text-emerald-400 text-xs font-normal mt-1">
                          Applied to every booking assigned to you, regardless of trip size or payment method.
                        </p>
                      </div>
                      <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 mt-4">
                        <p className="text-emerald-400 text-xs font-normal mb-1">Example Calculation</p>
                        <p className="text-white text-sm font-normal">
                          $1,000 base fare → <span className="text-emerald-400 font-medium">${fmtAmount(1000 * effectiveBackerRateRender)} earnings</span> ({Math.round(effectiveBackerRateRender * 100)}% of $1,000)
                        </p>
                      </div>
                      <div className="p-3 rounded-lg bg-white/5 border border-white/10 mt-2">
                        <p className="text-white/60 text-xs font-normal mb-1">Available Balance</p>
                        <p className="text-white text-sm font-normal">
                          Starting Balance + earnings from <span className="text-emerald-400">paid-off</span> trips − base fares of trips still being paid off.
                        </p>
                        <p className="text-white/40 text-[11px] font-normal mt-1">
                          A trip&apos;s funds (your capital + earnings on it) become available the moment the customer finishes paying it off.
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                        <p className="text-white/60 text-xs font-normal mb-1">Trips under $300</p>
                        <p className="text-white text-sm font-medium">40% (Slice Pay) / 30% (Defera Capital)</p>
                        <p className="text-emerald-400 text-xs font-normal mt-1">
                          {effectiveIsBackerRender ? `Backer earns ${Math.round(effectiveBackerRateRender * 100)}% of this fee` : 'Investor earns 14% of this fee'}
                        </p>
                      </div>
                      <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                        <p className="text-white/60 text-xs font-normal mb-1">Trips $300+</p>
                        <p className="text-white text-sm font-medium">8% service fee</p>
                        <p className="text-emerald-400 text-xs font-normal mt-1">
                          {effectiveIsBackerRender ? `Backer earns ${Math.round(effectiveBackerRateRender * 100)}% of this fee` : 'Investor earns 14% of this fee'}
                        </p>
                      </div>
                      <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 mt-4">
                        <p className="text-emerald-400 text-xs font-normal mb-1">Example Calculation</p>
                        <p className="text-white text-sm font-normal">
                          {effectiveIsBackerRender ? (
                            <>$1,000 trip → $80 service fee (8%) → <span className="text-emerald-400 font-medium">${fmtAmount(80 * effectiveBackerRateRender)} backer earnings</span> ({Math.round(effectiveBackerRateRender * 100)}% of $80)</>
                          ) : (
                            <>$1,000 trip → $80 service fee (8%) → <span className="text-emerald-400 font-medium">$11.20 investor earnings</span> (14% of $80)</>
                          )}
                        </p>
                      </div>
                      {effectiveIsBackerRender && (
                        <div className="p-3 rounded-lg bg-white/5 border border-white/10 mt-2">
                          <p className="text-white/60 text-xs font-normal mb-1">Available Balance</p>
                          <p className="text-white text-sm font-normal">
                            Starting Balance + earnings from <span className="text-emerald-400">paid-off</span> trips − base fares of trips still being paid off.
                          </p>
                          <p className="text-white/60 text-[12.5px] font-normal mt-1">
                            A trip&apos;s funds (your capital + earnings on it) become available the moment the customer finishes paying it off.
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div className={`grid gap-2 md:gap-4 ${
            effectiveIsBackerRender
              ? (effectiveStartingBalanceRender > 0 ? 'grid-cols-3' : 'grid-cols-2')
              : 'grid-cols-3'
          }`}>
            {!effectiveIsBackerRender && (
              <div className="p-2 md:p-4 rounded-xl bg-white/5 border border-white/10">
                <p className="text-white/60 text-[9px] md:text-xs font-normal mb-0.5 md:mb-1 uppercase tracking-wide">Capital Invested</p>
                <p className="text-white text-sm md:text-xl font-light">
                  ${totalCapitalInvested.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            )}
            {effectiveIsBackerRender && effectiveStartingBalanceRender > 0 && (
              <div className="p-2 md:p-4 rounded-xl bg-white/5 border border-white/10">
                <p className="text-white/60 text-[9px] md:text-xs font-normal mb-0.5 md:mb-1 uppercase tracking-wide">Starting Balance</p>
                <p className="text-white text-sm md:text-xl font-light">
                  ${effectiveStartingBalanceRender.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            )}
            <div className="p-2 md:p-4 rounded-xl bg-white/5 border border-white/10">
              <p className="text-white/60 text-[9px] md:text-xs font-normal mb-0.5 md:mb-1 uppercase tracking-wide">
                {isBaseFareBackerRender ? 'Backer Earnings' : 'Gains'}
              </p>
              <p className="text-green-400 text-sm md:text-xl font-light">
                +${totalGains.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div className="p-2 md:p-4 rounded-xl bg-green-500/10 border border-green-500/20">
              <p className="text-green-400/80 text-[9px] md:text-xs font-normal mb-0.5 md:mb-1 uppercase tracking-wide">Available Balance</p>
              <p className="text-green-400 text-sm md:text-xl font-light">
                ${withdrawableBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Bookings List - Robinhood Style - Compact */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-12"
        >
          <h2 className="text-white font-light text-xl mb-3 tracking-tight">Bookings</h2>
          <div className="space-y-0.5">
            {trips.length === 0 ? (
              <p className="text-white/60 text-sm font-normal text-center py-12">No bookings yet</p>
            ) : (
              trips.slice(0, bookingsToShow).map((trip, index) => {
                // Extract airline logo from flight data
                const airlineLogo = trip.flight_data?.slices?.[0]?.segments?.[0]?.marketing_carrier?.logo_symbol_url || 
                                   trip.flightData?.slices?.[0]?.segments?.[0]?.marketing_carrier?.logo_symbol_url;
                const airlineCode = trip.flight_data?.slices?.[0]?.segments?.[0]?.marketing_carrier?.iata_code || 
                                   trip.flightData?.slices?.[0]?.segments?.[0]?.marketing_carrier?.iata_code || 
                                   trip.departure_city?.substring(0, 2) || 'FL';
                
                // Get trip title - prioritize custom title over auto-generated
                const customTitle = trip.customTitle || trip.custom_title || 
                                   trip.flight_data?.custom_title || trip.flightData?.custom_title;
                const tripTitle = (customTitle && customTitle.trim() !== '') 
                  ? customTitle.trim() 
                  : `${getCityName(trip.departure_city)} → ${getCityName(trip.arrival_city)}`;
                
                return (
                  <div 
                    key={trip.id || index}
                    onClick={() => setSelectedTrip(trip)}
                    className="flex items-center gap-3 px-3 py-2.5 hover:bg-white/5 transition-colors rounded-lg cursor-pointer"
                  >
                    {/* Airline Logo - Real or Fallback */}
                    {airlineLogo ? (
                      <img 
                        src={airlineLogo} 
                        alt={airlineCode}
                        className="w-8 h-8 rounded-lg object-contain flex-shrink-0 p-0.5"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className={`w-8 h-8 rounded-lg bg-black flex-shrink-0 items-center justify-center ${airlineLogo ? 'hidden' : 'flex'}`}
                      style={{ display: airlineLogo ? 'none' : 'flex' }}
                    >
                      <span className="text-white/60 text-xs font-normal">
                        {airlineCode}
                      </span>
                    </div>
                    
                    {/* Route Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-normal truncate">
                        {tripTitle}
                      </p>
                      <p className="text-white/40 text-xs font-normal">
                        {effectiveIsBackerRender
                          ? (trip.assignedDate
                              ? `Assigned ${format(trip.assignedDate, 'MMM dd, yyyy')}`
                              : (trip.createdDate ? format(trip.createdDate, 'MMM dd, yyyy') : 'Date N/A'))
                          : (trip.createdDate ? format(trip.createdDate, 'MMM dd, yyyy') : 'Date N/A')}
                      </p>
                    </div>
                    
                    {/* Private Sale badge - no profit from service fee */}
                    {trip.flight_data?.promo_applied && (
                      <span className="px-2 py-0.5 rounded-md bg-brand-500/10 text-brand-300 border border-brand-500/20 text-[10px] font-normal flex-shrink-0">
                        Private Sale
                      </span>
                    )}
                    
                    {/* Revenue */}
                    <div className="text-right flex-shrink-0">
                      <p className="text-white text-sm font-normal">
                        +${fmtAmount(trip.investorEarnings)}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          {/* Show More Button */}
          {trips.length > bookingsToShow && (
            <div className="flex justify-center mt-6">
              <button
                onClick={() => setBookingsToShow(prev => prev + 50)}
                className="px-6 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 text-white text-sm font-normal rounded-lg transition-all"
              >
                Show More
              </button>
            </div>
          )}
        </motion.div>

        {/* Booking Details Modal */}
        {selectedTrip && (
          <Dialog open={!!selectedTrip} onOpenChange={(open) => !open && setSelectedTrip(null)}>
            <DialogContent className="bg-brand-950/95 border-brand-800/45 backdrop-blur-xl text-white w-full max-w-[calc(100vw-1rem)] sm:max-w-md mx-auto rounded-3xl max-h-[calc(100dvh-2rem)] sm:max-h-[90vh] overflow-y-auto overscroll-contain">
              <DialogHeader>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <DialogTitle className="text-white font-light text-lg mb-1">
                      {(() => {
                        const customTitle = selectedTrip.customTitle || selectedTrip.custom_title || 
                                           selectedTrip.flight_data?.custom_title || selectedTrip.flightData?.custom_title;
                        return (customTitle && customTitle.trim() !== '') 
                          ? customTitle.trim() 
                          : `${getCityName(selectedTrip.departure_city)} → ${getCityName(selectedTrip.arrival_city)}`;
                      })()}
                    </DialogTitle>
                    <DialogDescription className="text-white/60 text-sm font-normal">
                      Booked {selectedTrip.createdDate ? format(selectedTrip.createdDate, 'MMM dd, yyyy') : 'N/A'}
                      {effectiveIsBackerRender && selectedTrip.assignedDate &&
                        selectedTrip.assignedDate.getTime() !== selectedTrip.createdDate?.getTime() && (
                          <span className="block text-white/40 text-xs font-normal mt-0.5">
                            Assigned to you {format(selectedTrip.assignedDate, 'MMM dd, yyyy')}
                          </span>
                        )}
                    </DialogDescription>
                    {(() => {
                      // Investor cash-flow context: surface the departure date here so the
                      // backer/investor can see how long until the booking is fully paid
                      // (and therefore when their earnings on it become withdrawable).
                      // Falls back gracefully across all the shapes departure can take on
                      // a booking (entity-derived date, raw flight_data, payment_schedule).
                      const depRaw =
                        selectedTrip.departure_date ||
                        selectedTrip.departureDate ||
                        selectedTrip.flight_data?.payment_schedule?.departure_date ||
                        selectedTrip.flight_data?.slices?.[0]?.segments?.[0]?.departing_at ||
                        selectedTrip.flight_data?.departure_date ||
                        null;
                      if (!depRaw) return null;
                      let depDate;
                      try {
                        depDate = parseISO(typeof depRaw === 'string' ? depRaw : String(depRaw));
                        if (Number.isNaN(depDate.getTime())) return null;
                      } catch {
                        return null;
                      }
                      return (
                        <div className="mt-1.5 flex items-center gap-1.5 text-white/80 text-sm font-normal">
                          <PlaneTakeoff className="w-3.5 h-3.5 text-white/60" />
                          <span className="text-white/50">Departs</span>
                          <span className="text-white">{format(depDate, 'EEE, MMM dd, yyyy')}</span>
                        </div>
                      );
                    })()}
                  </div>
                  {selectedTrip.flight_data?.promo_applied && (
                    <span className="px-2 py-1 rounded-md bg-brand-500/10 text-brand-300 border border-brand-500/20 text-xs font-normal flex-shrink-0">
                      Private Sale
                    </span>
                  )}
                </div>
              </DialogHeader>

              <div className="space-y-4 mt-4">
                {/* Price Breakdown */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <h3 className="text-white font-normal text-base">Price Breakdown</h3>
                    <Dialog>
                      <DialogTrigger asChild>
                        <button className="text-white/60 hover:text-white transition-colors">
                          <Info className="w-4 h-4" />
                        </button>
                      </DialogTrigger>
                      <DialogContent className="bg-brand-950/95 border-brand-800/45 backdrop-blur-xl text-white max-w-md rounded-3xl">
                        <DialogHeader>
                          <DialogTitle className="text-white font-light text-lg">Fee Explanations</DialogTitle>
                          <DialogDescription className="text-white/60 text-sm font-normal mt-2">
                            Understanding how each fee is calculated
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-3 mt-4">
                          <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                            <p className="text-white text-sm font-medium mb-1">Base Fare</p>
                            <p className="text-white/60 text-[13px] font-normal">
                              The original flight price from the airline, before any additional fees.
                            </p>
                          </div>
                          <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                            <p className="text-white text-sm font-medium mb-1">Service Fee</p>
                            <p className="text-white/60 text-[13px] font-normal">
                              Defera&apos;s service fee on base fare: 40% under $300 on Slice Pay, 30% under $300 on Defera Capital; 8% for $300+.
                            </p>
                          </div>
                          <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                            <p className="text-white text-sm font-medium mb-1">Hold Fee (15%)</p>
                            <p className="text-white/60 text-[13px] font-normal">
                              A 15% hold fee applied to the grand total (base fare + service fee) to secure the booking and cover payment processing.
                            </p>
                          </div>
                          <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                            <p className="text-emerald-400 text-sm font-normal mb-1">
                              {isBackerUser ? 'Backer Fee' : 'Investor Fee'}
                            </p>
                            <p className="text-white/60 text-[13px] font-normal">
                              {isBackerUser
                                ? (backerEarningModel === 'base_fare_share'
                                    ? `Your share: ${Math.round(backerEarningRate * 100)}% of the base fare. This is your earnings from this booking.`
                                    : `Your share: ${Math.round(backerEarningRate * 100)}% of the service fee. This is your earnings from this booking.`)
                                : 'Your share: 14% of the service fee. This is your earnings from this booking.'}
                            </p>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>

                  {(() => {
                    // Derive everything from the same helper as the earnings calc above,
                    // so the modal's Base Fare / Service Fee % / Hold Fee % / Total Price
                    // exactly match what the customer actually paid + what gets paid out to
                    // backers/investors. See `deriveBookingPricing()` for the rationale.
                    const pricing = deriveBookingPricing(selectedTrip);
                    const {
                      isMilesOnly,
                      isFlightHotel,
                      isDeferaCapital,
                      baseFare,
                      serviceFee,
                      serviceFeeRate,
                      holdFee,
                      holdFeeRate: holdFeeRateRaw,
                      totalWithHold,
                      insurancePremium,
                      planTotal,
                    } = pricing;
                    const holdFeeRate = Math.round(holdFeeRateRaw * 100);
                    // Earnings depend on role + (for backers) their earning model.
                    // Customer-facing pricing is unchanged — this only affects what the backer earns.
                    // - Investors: 14% of service fee
                    // - Backers (service_fee_share): rate * service fee (default 25%)
                    // - Backers (base_fare_share): rate * base fare (e.g. 15%)
                    const isBaseFareBacker = isBackerUser && backerEarningModel === 'base_fare_share';
                    const feeEarnings = isBackerUser
                      ? (isBaseFareBacker ? baseFare * backerEarningRate : serviceFee * backerEarningRate)
                      : serviceFee * INVESTOR_FEE_RATE_ON_SERVICE_FEE;
                    // Miles: 2 per $1 of totalWithHold. Miles-only earns no miles.
                    const milesEarned = isMilesOnly ? 0 : Math.floor(totalWithHold * 2);
                    const mileageLiability = (milesEarned * MILES_LIABILITY_CENTS_PER_MILE) / 100;

                    return (
                      <div className="space-y-2">
                        <div className="flex justify-between items-center py-2 border-b border-white/5">
                          <span className="text-white/60 text-sm font-normal">Base Fare</span>
                          <span className="text-white text-sm font-normal">
                            ${baseFare.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-white/5">
                          <span className="text-white/60 text-sm font-normal">
                            Service Fee {(isFlightHotel || selectedTrip.flight_data?.promo_applied) ? '' : `(${Math.round(serviceFeeRate * 100)}%)`}
                          </span>
                          <span className={(isFlightHotel || selectedTrip.flight_data?.promo_applied) ? 'text-green-400 text-sm font-normal' : 'text-white text-sm font-normal'}>
                            {(isFlightHotel || selectedTrip.flight_data?.promo_applied) ? '$0.00' : `$${serviceFee.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                          </span>
                        </div>
                        {holdFee > 0 && (
                          <div className="flex justify-between items-center py-2 border-b border-white/5">
                            <span className="text-white/60 text-sm font-normal">Hold Fee ({holdFeeRate}%)</span>
                            <span className="text-white text-sm font-normal">
                              ${holdFee.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                        )}
                        {!isMilesOnly && (
                          <>
                            <div className="flex justify-between items-center py-2 border-b border-white/5">
                              <span className="text-brand-400/80 text-sm font-normal">Miles Earned</span>
                              <span className="text-brand-400 text-sm font-normal">{milesEarned.toLocaleString()} miles</span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b border-white/5">
                              <span className="text-brand-400/80 text-sm font-normal">Miles Liability</span>
                              <span className="text-brand-400 text-sm font-normal">${mileageLiability.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                          </>
                        )}
                        <div className="flex justify-between items-center py-2 border-b border-emerald-500/20 bg-emerald-500/5 rounded-lg">
                          <span className="text-emerald-400 text-sm font-normal">
                            {isBackerUser
                              ? (isBaseFareBacker
                                  ? `Backer Fee (${Math.round(backerEarningRate * 100)}% of base fare)`
                                  : `Backer Fee (${Math.round(backerEarningRate * 100)}% of service)`)
                              : 'Investor Fee (14% of service)'}
                          </span>
                          <span className="text-emerald-400 text-sm font-normal">
                            +${feeEarnings.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                        {insurancePremium > 0 && (
                          // Collected for Faye: outside the base fare, fees and
                          // earnings above, but part of what the customer pays.
                          <div className="flex justify-between items-center py-2 border-b border-white/5">
                            <span className="text-white/60 text-sm font-normal">Travel insurance (Faye)</span>
                            <span className="text-white text-sm font-normal">
                              ${insurancePremium.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between items-center py-3 border-t border-white/10 mt-2">
                          <span className="text-white text-base font-medium">Total Price</span>
                          <span className="text-white text-base font-medium">
                            ${planTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Payment Schedule — read-only cash-flow view for backers/investors.
                    Derived here (instead of reusing PaymentScheduleDisplay) because:
                      1. PaymentScheduleDisplay pulls live Stripe state + exposes
                         change-card / extension / make-payment actions that
                         investors must not see or trigger.
                      2. Investors only need projected timing: deposit date, plan
                         cadence, first/final payment, and the per-payment amount.
                    We prefer the booking's stored payment_schedule (locked at
                    checkout) and fall back to a sensible default derived from
                    booking date + departure + pre-hold subtotal so older bookings
                    still render something useful. */}
                {(() => {
                  const flightData = selectedTrip.flight_data || selectedTrip.flightData || {};
                  const storedSchedule = flightData.payment_schedule || null;

                  const pricing = deriveBookingPricing(selectedTrip);
                  const { preHoldSubtotal, holdFeeRate, isDeferaZero, isDeferaCapital, isReserve, isMilesOnly, insurancePremium } = pricing;

                  // Reserve and pure miles bookings have no installment schedule.
                  if (isReserve || isMilesOnly) return null;

                  const depRaw =
                    storedSchedule?.departure_date ||
                    selectedTrip.departure_date ||
                    selectedTrip.departureDate ||
                    flightData.slices?.[0]?.segments?.[0]?.departing_at ||
                    flightData.departure_date ||
                    null;
                  const bookingRaw =
                    storedSchedule?.booking_date ||
                    selectedTrip.createdAt ||
                    selectedTrip.created_at ||
                    null;

                  if (!depRaw || !bookingRaw || !(preHoldSubtotal > 0)) return null;

                  // When no stored deposit %, derive a sensible default for the trip's lead time.
                  // DC uses the lowest available tier for that band (5% at 90+, 20% at 60–89, etc.);
                  // Slice Pay keeps the historical flat 20% fallback; Defera Zero is always 0%.
                  let depositPercentage;
                  if (storedSchedule && Number.isFinite(parseFloat(storedSchedule.deposit_percentage))) {
                    depositPercentage = parseFloat(storedSchedule.deposit_percentage);
                  } else if (isDeferaZero) {
                    depositPercentage = 0;
                  } else if (isDeferaCapital) {
                    const leadDays = bookingRaw && depRaw
                      ? Math.floor((new Date(depRaw) - new Date(bookingRaw)) / 86_400_000)
                      : NaN;
                    depositPercentage = getDefaultInstallmentDepositPercentForLeadDays(leadDays, { deferaCapital: true });
                  } else {
                    depositPercentage = 20;
                  }
                  const paymentPlan =
                    (storedSchedule?.payment_plan === 'biweekly' ? 'biweekly' : 'weekly');
                  const daysBeforeDepartureForLastPayment = isDeferaZero
                    ? DEFERA_ZERO_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE
                    : (isDeferaCapital
                        ? DEFERA_CAPITAL_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE
                        : SLICE_PAY_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE);

                  let schedule;
                  try {
                    schedule = calculatePaymentSchedule({
                      bookingDate: bookingRaw,
                      departureDate: depRaw,
                      totalWithFee: storedSchedule?.total_with_fee || preHoldSubtotal,
                      depositPercentage,
                      firstPaymentDays: isDeferaZero ? 7 : undefined,
                      daysBeforeDepartureForLastPayment,
                      holdFeeRate: Number.isFinite(storedSchedule?.hold_fee_rate)
                        ? storedSchedule.hold_fee_rate
                        : holdFeeRate,
                      deferaCapitalDepositTiers: isDeferaCapital,
                      respectStoredDepositPercentage: !!storedSchedule,
                      // A Faye premium rides on the installments after the deposit.
                      insurancePremium: insurancePremium || 0,
                    });
                  } catch {
                    return null;
                  }

                  if (!schedule?.available) {
                    // Too close to departure for an installment plan — show
                    // just deposit/total so investor still sees timing context.
                    return (
                      <div>
                        <h3 className="text-white font-normal text-base mb-3">Payment Schedule</h3>
                        <div className="p-3 rounded-lg bg-white/5 border border-white/10 text-white/60 text-[13px] font-normal">
                          No installment plan on this booking — paid in full at checkout.
                        </div>
                      </div>
                    );
                  }

                  const activePlan = paymentPlan === 'biweekly' ? schedule.biweeklyPlan : schedule.weeklyPlan;
                  const planLabel = paymentPlan === 'biweekly' ? 'Bi-weekly' : 'Weekly';
                  const bookingDateObj = parseISO(bookingRaw);
                  const isPaidOff = selectedTrip.isPaidOff || resolveBookingStatus(selectedTrip) === 'completed';

                  return (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-white font-normal text-base">Payment Schedule</h3>
                        {isPaidOff && (
                          <span className="px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 text-[10px] font-normal uppercase tracking-wider">
                            Paid Off
                          </span>
                        )}
                      </div>

                      <div className="space-y-2">
                        <div className="flex justify-between items-center py-2 border-b border-white/5">
                          <span className="text-white/60 text-sm font-normal">
                            Deposit ({Math.round(schedule.depositPercentage)}%)
                          </span>
                          <div className="text-right">
                            <div className="text-white text-sm font-normal">
                              ${schedule.depositAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                            <div className="text-white/40 text-xs font-normal">
                              {format(bookingDateObj, 'MMM dd, yyyy')}
                            </div>
                          </div>
                        </div>

                        {activePlan?.available && activePlan.paymentCount > 0 && (
                          <>
                            <div className="flex justify-between items-center py-2 border-b border-white/5">
                              <span className="text-white/60 text-sm font-normal">
                                {planLabel} · {activePlan.paymentCount}× payments
                              </span>
                              <span className="text-white text-sm font-normal">
                                ${activePlan.paymentAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                <span className="text-white/40 text-xs font-normal"> each</span>
                              </span>
                            </div>

                            <div className="grid grid-cols-2 gap-2 py-2">
                              <div className="p-2.5 rounded-lg bg-white/[0.03] border border-white/5">
                                <div className="flex items-center gap-1.5 mb-1">
                                  <Calendar className="w-3 h-3 text-white/40" />
                                  <p className="text-white/40 text-[10px] font-normal uppercase tracking-wider">First Payment</p>
                                </div>
                                <p className="text-white text-sm font-normal">
                                  {activePlan.firstPaymentDate
                                    ? format(parseISO(activePlan.firstPaymentDate), 'MMM dd, yyyy')
                                    : '—'}
                                </p>
                              </div>
                              <div className="p-2.5 rounded-lg bg-white/[0.03] border border-white/5">
                                <div className="flex items-center gap-1.5 mb-1">
                                  <CalendarClock className="w-3 h-3 text-emerald-400/70" />
                                  <p className="text-white/40 text-[10px] font-normal uppercase tracking-wider">Final Payment</p>
                                </div>
                                <p className="text-white text-sm font-normal">
                                  {activePlan.finalPaymentDate
                                    ? format(parseISO(activePlan.finalPaymentDate), 'MMM dd, yyyy')
                                    : '—'}
                                </p>
                              </div>
                            </div>

                            <details className="group">
                              <summary className="cursor-pointer list-none text-white/50 hover:text-white/80 transition-colors text-xs font-normal py-1.5 flex items-center gap-1">
                                <span className="group-open:hidden">Show all {activePlan.paymentCount} payment dates</span>
                                <span className="hidden group-open:inline">Hide payment dates</span>
                              </summary>
                              <div className="mt-2 max-h-48 overflow-y-auto pr-1 space-y-1">
                                {activePlan.schedule.map((d, i) => (
                                  <div
                                    key={d}
                                    className="flex justify-between items-center px-2.5 py-1.5 rounded-md bg-white/[0.02] border border-white/[0.04]"
                                  >
                                    <span className="text-white/50 text-xs font-normal tabular-nums">
                                      #{i + 1} · {format(parseISO(d), 'EEE, MMM dd, yyyy')}
                                    </span>
                                    <span className="text-white/80 text-xs font-normal tabular-nums">
                                      ${activePlan.paymentAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </details>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Booking Reference */}
                {(selectedTrip.bookingReference || selectedTrip.booking_reference || selectedTrip.tripReference || selectedTrip.trip_reference) && (
                  <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                    <p className="text-white/60 text-xs font-normal mb-1">Booking Reference</p>
                    <p className="text-white text-sm font-mono">
                      {selectedTrip.bookingReference || selectedTrip.booking_reference || selectedTrip.tripReference || selectedTrip.trip_reference}
                    </p>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
}
